import { useEffect, useMemo, useState } from 'react';
import { type TCTemplateRecord } from 'src/api/generated';
import { type MappingValue } from 'src/modules/automations/components/molecules/create-trigger-modal/mapping-row';
import {
  areRequiredFieldsMapped,
  buildDefaultMapping,
  buildEventOptionsFromTriggerSchema,
  buildInstructions,
  getTemplateSchemaMetadata,
} from '../utils/templates/template-mapping.util';
import {
  type LiteralType,
  type TemplateDefinition,
} from '../utils/templates/template-definition.type';

type UseTemplateMappingParams = {
  selectedTemplate: TCTemplateRecord | null;
  presetMapping?: Record<string, MappingValue>;
  triggerDataSchema?: unknown;
};

export const useTemplateMapping = ({
  selectedTemplate,
  presetMapping,
  triggerDataSchema,
}: UseTemplateMappingParams) => {
  const [mapping, setMapping] = useState<Record<string, MappingValue>>({});

  const {
    templateDefinition,
    fieldKeys,
    requiredFields,
    fieldExpectedLiteralTypes,
  }: {
    templateDefinition?: TemplateDefinition;
    fieldKeys: string[];
    requiredFields: Set<string>;
    fieldExpectedLiteralTypes: Record<string, LiteralType>;
  } = useMemo(
    () => getTemplateSchemaMetadata(selectedTemplate),
    [selectedTemplate]
  );

  useEffect(() => {
    setMapping(buildDefaultMapping(selectedTemplate, presetMapping));
  }, [selectedTemplate, presetMapping]);

  const eventPathOptions = useMemo(
    () => buildEventOptionsFromTriggerSchema(triggerDataSchema),
    [triggerDataSchema]
  );

  const allRequiredMapped = useMemo(
    () => areRequiredFieldsMapped(mapping, requiredFields),
    [mapping, requiredFields]
  );

  return {
    mapping,
    setMapping,
    fieldKeys,
    requiredFields,
    fieldExpectedLiteralTypes,
    eventPathOptions,
    allRequiredMapped,
    templateDefinition,
    buildInstructions: () => buildInstructions(fieldKeys, mapping),
  };
};
